import { SelectInput } from './select.mjs';
import FormanConfig from '../formanConfig.mjs';
import { registerCustomElement, setInstructionsDefault, create, resolveAdminUrl } from '../utils.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { Picker } from '../controls/picker.mjs';
import EventManager from '../eventManager.mjs';
import events from '../events.mjs';
import classes from './scenario.module.scss';
import { ScenarioIconGroup } from './scenario-icon-group.mjs';
import common from '../common.module.scss';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import EVENTS from '../events.mjs';
import { html, render } from 'lit-html';
import Loader from '../loader.mjs';
import Features from '../features.mjs';

const config = FormanConfig.config.fields.scenario;

const getScenarioIdWithoutPrefix = (scenarioId) => {
	if (typeof scenarioId === 'string' && scenarioId.startsWith('SCN_')) {
		return scenarioId.substring(4);
	}

	return scenarioId;
};

const mapScenarioData = (data) => {
	return {
		value: getScenarioIdWithoutPrefix(data.value || data.id),
		label: data.label || data.name,
		active: data.active || data.isActive,
		description: data.description || '',
		usedPackages: data.usedPackages || [],
		type: data.type || 'scenario',
	};
};

const renderScenarioOption = (
	element,
	scenario,
	repository,
	{ isDisabled = false, showPackages = false, showActiveBadge = false, scenarioType } = {},
) => {
	element.innerHTML = '';

	if (isDisabled) {
		element.classList.add('dmo-cursor-not-allowed');
		element.toggleAttribute('disabled', true);
		const tooltipTitle =
			scenario.type === 'scenario'
				? I18n.l('scenarioList.scenarioDisabledInList')
				: I18n.l('scenarioList.moduleDisabledInList');

		jQuery(element).tooltip({ title: tooltipTitle, placement: 'top' });
	}

	const header = create('div.dmo-gap-8.dmo-items-center.dmo-flex');

	if (scenario.usedPackages.length > 0 && showPackages) {
		const iconGroup = ScenarioIconGroup.create(
			scenarioType === 'tool'
				? {
						packages: scenario.usedPackages.slice(1, 2),
						visiblePackagesCount: 1,
						repository,
				  }
				: {
						packages: scenario.usedPackages,
						repository,
				  },
		);

		header.append(iconGroup);
	}

	const name = create(`span.${classes.optionName} ${scenario.label}`);
	header.append(name);

	if (showActiveBadge) {
		const pill = create(`span.dmo-ml-auto.${common.pill}`);

		pill.textContent = scenario.active ? I18n.l('scenario.active') : I18n.l('scenario.inactive');
		pill.classList.toggle(common.pillSuccess, scenario.active);
		pill.classList.toggle(common.pillNeutral, !scenario.active);

		header.append(pill);
	}

	element.append(header);

	if (scenario.description) {
		const description = create(`div.${classes.scenarioOptionDescription}`);
		description.textContent = scenario.description;

		element.append(description);
	}
};

export class ScenarioInput extends SelectInput {
	get hasModeSwitch() {
		return this.mappable;
	}

	get allowApiCall() {
		return true;
	}

	get mode() {
		return super.mode;
	}

	set mode(value) {
		super.mode = value;

		this.handleMapToggle(value);
	}

	set descriptionEdit(value) {
		if (value === true) {
			this.setAttribute('description-edit', value);

			if (this.isConnected && this.value) {
				this._showEditDescription();
			}
		} else {
			this.removeAttribute('description-edit');
			this._hideEditDescription();
		}
	}

	get descriptionEdit() {
		return this.hasAttribute('description-edit');
	}

	getState() {
		const state = super.getState();

		if (this.mode === 'pick') {
			const selected = this._control.getSelectedElements();
			const label = this._control.getPickerDisplay()?.textContent;

			state.label = label;
			const stateData = this.getStateData(selected[0]);

			if (stateData) {
				state.data = stateData;
			}
		}

		return state;
	}

	async _build(instructions, value, metadata) {
		const picker = create('imt-picker');

		if (instructions.scenarioType === 'tool') {
			picker.classList.add('imt-picker-v2');
		}

		if (!metadata.build) {
			metadata.build = {};
		}
		metadata.build.picker = picker;
		picker.register(Picker.COMPONENTS.OPTION_DISPLAY_VALUE, (option) => {
			return getScenarioIdWithoutPrefix(option?.value);
		});

		picker.register(Picker.COMPONENTS.OPTION_RENDERER, (element, data) => {
			const scenario = mapScenarioData(data);
			const isDisabled = instructions.options.ignore.includes(scenario.value);

			return renderScenarioOption(element, scenario, this.form.meta.repository, {
				isDisabled,
				showPackages: instructions.showPackages,
				showActiveBadge: instructions.showActiveBadge,
				scenarioType: instructions.scenarioType,
			});
		});

		// Displays scenario label in display when it gets selected
		picker.register(Picker.COMPONENTS.DISPLAY_CHANGED, (displayElement, value) => {
			const scenario = picker.getOptionData();

			if (scenario) {
				displayElement.textContent = mapScenarioData(scenario).label;
			}
		});

		if (instructions.allowCreateOption) {
			picker.actionOption = instructions.createOptionAction || {
				label: I18n.l('scenario.createScenario'),
				action: this._createHandler(instructions),
			};
		}

		await super._build(instructions, value, metadata);
	}

	extendInstructions(instructions, value, metadata) {
		const storeQuery = new URLSearchParams();

		storeQuery.append('teamId', this.getTeamId(instructions));
		storeQuery.append('pg[limit]', config.list.limit);

		instructions = setInstructionsDefault('grouped', instructions, config.list);
		instructions = setInstructionsDefault('showSearcher', instructions, config.list);
		instructions = setInstructionsDefault('showValue', instructions, config.list);
		instructions.searcherPlaceholder = instructions.searcherPlaceholder || I18n.l('scenario.searchLabel');
		// TODO CDM-13800
		instructions.options.store = instructions.options.store || `${config.list.path}?${storeQuery}`;
		if (Features.isOn('list-modules-as-tools') && !instructions.options.store.includes('&type=')) {
			instructions.options.store += instructions.options.store.includes('?') ? '&type=all' : '?type=all';
		}
		instructions.options.wrapper = instructions.options.wrapper || config.list.wrapper;
		instructions.options.value = instructions.options.value || config.list.value;
		instructions.options.label = instructions.options.label || config.list.label;
		instructions.options.ignore = instructions.options.ignore || [];
		instructions.options.team = this.getTeamId(instructions);
		instructions.options.folder = window['Inspector']?.instance?.root?.data('folderId');
		instructions.displayGroup = false;
		instructions.forceReload = true;

		return instructions;
	}
	updateScenarioInfo(picker) {
		const scenarioInfo = picker?.getOptionData();
		const selected = picker?.getSelectedElements()[0];
		const group = selected?.closest('imt-optgroup');
		const groupName = group?.getAttribute('label');

		if (scenarioInfo) {
			scenarioInfo.groupName = groupName;
			this.scenarioInfo = scenarioInfo;
			this.renderScenarioInfo(scenarioInfo);
		}
	}

	connectedCallback() {
		setTimeout(() => {
			EventManager.addListener(this._control, events.PICKER.CHANGED, (e) => {
				this.updateScenarioInfo(e?.target);
				if (this.descriptionEdit) {
					this._showEditDescription();
				}
			});
			this.updateScenarioInfo(this._control);
			if (this.value && this.descriptionEdit) {
				this._showEditDescription();
			}
		});
	}

	handleMapToggle(value) {
		if (value === 'map') {
			const currentScenarioInfo = this.querySelector(`.${classes.scenarioInfo}`);

			if (currentScenarioInfo) {
				currentScenarioInfo.remove();
			}
		} else {
			this.updateScenarioInfo(this._control);
		}
	}

	renderScenarioInfo(data) {
		const currentInfo = this.querySelector(`.${classes.scenarioInfo}`);

		if (currentInfo) {
			currentInfo.remove();
		}

		if (!data) {
			return;
		}

		const scenarioInfo = mapScenarioData(data);

		const scenarioId = scenarioInfo.value;
		const scenarioName = scenarioInfo.label;

		if (!scenarioId || !scenarioName) {
			return;
		}

		const showGroup = scenarioInfo.groupName?.toLowerCase && scenarioInfo.groupName.toLowerCase() !== 'uncategorized';
		const previewUrl = resolveAdminUrl(`${this.getTeamId(this._instructions)}/scenarios/${scenarioId}/edit`);

		const html = `
			<div class="${classes.scenarioInfo} mb-3" data-testid="scenario-info">
				<div class="d-flex justify-content-between w-100">
					<div>
						${
							scenarioInfo.active
								? `<span class="badge badge-success">${I18n.l('scenario.active')}</span>`
								: `<span class="badge badge-secondary">${I18n.l('scenario.inactive')}</span>`
						}
						<span class="text-muted small ml-2 ${classes.scenarioId}">ID:${scenarioId}</span>
					</div>
					<div>
						<a class="${classes.scenarioHref}" data-testid="scenario-href" href="
							${previewUrl}" target="_blank">${I18n.l(
								'scenario.preview',
							)} <i class="ml-1 fa-solid fa-arrow-up-right-from-square"></i></a>
					</div>
				</div>
				<div class="mt-1">
					${
						showGroup
							? `<span class="text-muted small"><i class="fal fa-fw fa-folder mr-1"></i>${scenarioInfo.groupName} / </span>`
							: ''
					}
					<span>${scenarioName}</span>
				</div>
			</div>
		`;

		this.querySelector('label')?.insertAdjacentHTML('afterend', html);
		EventManager.dispatch(this, events.FORM.RESIZE, { bubbles: true });
	}

	initEditDescription() {
		if (this.querySelector(`.${classes.scenarioDescription}`)) {
			return;
		}

		const wrapperElm = document.createElement('div');
		wrapperElm.classList.add(classes.scenarioDescription);
		this.helpElement?.after(wrapperElm);

		this.registerCustomValidator(() => {
			if (wrapperElm.hidden || !this.descriptionEdit) {
				return {
					valid: true,
				};
			}

			const descriptionElm = wrapperElm.querySelector('textarea[name="description"]');

			if (!descriptionElm?.value?.length) {
				descriptionElm.classList.add(classes.invalid);
				return {
					valid: false,
				};
			} else {
				descriptionElm.classList.remove(classes.invalid);
			}

			return {
				valid: true,
			};
		});

		const onChange = async (e) => {
			if (e.target.value?.length > 0) {
				try {
					this.scenarioInfo.description = e.target.value;
					await Loader.guardedLoad(
						config.patch.path,
						{
							context: {
								scenarioId: getScenarioIdWithoutPrefix(this.scenarioInfo.value),
							},
							method: 'PATCH',
							body: {
								description: e.target.value,
							},
						},
						true,
					);
				} catch (error) {
					this.form._handleError(error);
				}
			}
		};

		const template = html`
			<label class="mt-4"
				><span class="label-text-content"
					>${I18n.l('scenario.description')}<span class="required-asterisk text-danger">*</span></span
				>
			</label>

			<textarea
				@change=${onChange}
				class="form-control"
				name="description"
				rows="4"
				.value=${this._control?.getOptionData()?.description || ''}
			></textarea>

			<div class="form-validations ${classes.validationMessage}">${I18n.l('scenario.missingDescription')}</div>
			<div class="${classes.hint} form-text text-muted help-text">${I18n.l('scenario.descriptionHint')}</div>
		`;
		render(template, wrapperElm);

		EventManager.addListener(this._control, EVENTS.PICKER.CHANGED, () => {
			const scenarioInfo = this._control?.getOptionData();
			const descriptionInput = this.querySelector('textarea[name="description"]');

			if (descriptionInput) {
				descriptionInput.value = scenarioInfo?.description || '';
			}
		});
	}

	_hideEditDescription() {
		const descriptionElm = this.querySelector(`.${classes.scenarioDescription}`);
		if (descriptionElm) {
			descriptionElm.hidden = true;
		}
	}

	_showEditDescription() {
		const descriptionElm = this.querySelector(`.${classes.scenarioDescription}`);
		if (descriptionElm) {
			descriptionElm.hidden = false;
		} else {
			this.initEditDescription();
		}
	}

	_createHandler(instructions) {
		return async (target) => {
			if (this.panel) return;

			this.disabled = true;

			if (config?.instructions?.create?.redirect) {
				window.location.href = config.instructions.create.redirect;
				return;
			}

			this.panel = new RemoteForm(target);

			this.panel.addEventListener(EVENTS.FORM.SUBMITTED, async (event) => {
				this.disabled = false;
				const scenario = event.detail.data.scenario;
				const option = {
					[instructions?.options?.value || 'value']: 'SCN_' + scenario.id,
					label: scenario.name,
					name: scenario.name,
					description: scenario.description,
					usedPackages: scenario.usedPackages,
				};

				await this.addGroupOption(scenario.folderId, option);
				window.open(`/${scenario.teamId}/scenarios/${scenario.id}/edit`, '_blank');
			});

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;
			});

			const instructionsQuery = new URLSearchParams();
			const metadata = {};

			instructionsQuery.append('teamId', instructions.options.team);
			if (instructions.options.folder) {
				instructionsQuery.append('folderId', instructions.options.folder);
			}

			if (instructions.options?.team != null) {
				metadata.teamId = instructions.options.team;
			}

			await this.panel.build(
				{
					name: I18n.l('scenario.createScenario'),
					src: `${config.instructions.create.path}?${instructionsQuery}`,
					theme: instructions.theme,
				},
				metadata,
			);

			this.panel.open();
		};
	}
}

registerCustomElement('imt-input-scenario', ScenarioInput);
